/**
 * API Error Types
 */

export interface ApiErrorResponse {
  error: string;
  details?: unknown[];
}

/**
 * RFC-9110 / RFC-7807 problem-details body, as produced by ASP.NET's
 * `Results.Problem(...)` (served with `application/problem+json`).
 * The operator-facing message lives in `detail` (falling back to `title`);
 * `status` / `type` are machine fields and must never be shown to a user.
 */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  [key: string]: unknown;
}

/**
 * Generic fallback shown when an error carries no human-readable message.
 * Kept deliberately neutral so it is safe at any error site.
 */
const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly details?: unknown[]
  ) {
    super(message);
    this.name = 'ApiError';
  }

  static fromResponse(status: number, body: ApiErrorResponse): ApiError {
    return new ApiError(status, body.error, body.details);
  }

  /**
   * Build an ApiError from an RFC-9110 problem-details body. The thrown
   * `message` is the human-readable `detail` (then `title`), never the
   * raw JSON or the numeric status.
   */
  static fromProblem(status: number, body: ProblemDetails): ApiError {
    const message = body.detail ?? body.title ?? GENERIC_ERROR_MESSAGE;
    return new ApiError(status, message);
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isUnauthorised(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isValidationError(): boolean {
    return this.status === 400;
  }

  get isServerError(): boolean {
    return this.status >= 500;
  }
}

/**
 * Does this string look like a raw JSON object/array rather than prose?
 * Used as a last-resort guard so a serialised problem-details blob can
 * never leak onto an operator-facing screen.
 */
function looksLikeRawJson(value: string): boolean {
  const trimmed = value.trim();
  return (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  );
}

/**
 * Turn any thrown value into a single, friendly, operator-safe message.
 *
 * This is the one place that decides what a human sees when a request
 * fails. It prefers the problem-details `detail`, then `title`, then a
 * plain Error message — and it will NEVER surface raw JSON or an HTTP
 * status code. If a serialised problem-details blob has somehow ended up
 * as the message (e.g. an `application/problem+json` body that slipped
 * through as text), this parses it back out or falls back to a generic
 * line rather than showing braces to the customer.
 */
export function formatProblemError(
  error: unknown,
  fallback: string = GENERIC_ERROR_MESSAGE
): string {
  if (error instanceof ApiError) {
    return cleanMessage(error.message, fallback);
  }

  if (error instanceof Error) {
    return cleanMessage(error.message, fallback);
  }

  if (typeof error === 'string') {
    return cleanMessage(error, fallback);
  }

  return fallback;
}

/**
 * Normalise a candidate message: if it is empty, looks like raw JSON, or
 * is literally `"undefined"`, recover the human part (parsing problem
 * details where possible) or fall back.
 */
function cleanMessage(raw: string, fallback: string): string {
  const value = raw?.trim();
  if (!value || value === 'undefined' || value === 'null') {
    return fallback;
  }

  if (looksLikeRawJson(value)) {
    try {
      const parsed = JSON.parse(value) as ProblemDetails;
      const recovered = parsed.detail ?? parsed.title ?? parsed.error;
      if (typeof recovered === 'string' && recovered.trim()) {
        return recovered.trim();
      }
    } catch {
      // Not parseable JSON — fall through to the generic fallback below
      // rather than print braces to the operator.
    }
    return fallback;
  }

  return value;
}
